import { ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { isAccountOnly } from "@zooi/domain";
import { api, q } from "../api.ts";
import { useRefresh } from "../components.tsx";
import { AREA_LABELS as A, INBOX_LABELS as I, PRODUCT_SOURCE_LABELS as L, PROFILE_AREA_LABELS as P, PROFILE_DESIGN as D, reasonText } from "../labels.ts";
import { useCurrentProduct } from "../product-filter.tsx";
import { Lessons } from "../lessons.tsx";
import { LastRead, ProductSources, ProfileFacts, ProfileProperties, Readiness } from "../product-profile.tsx";
import { ProductRules } from "../product-rules.tsx";
import { reviewItems, SourceReview } from "../source-review.tsx";
import { Button, Callout, ErrorText, Page, Quiet, TabPanel, Tabs } from "../ui.tsx";
import { ProductMark } from "../ui/props.tsx";
import { ServiceLogo } from "../ui/service-logo.tsx";
import { Skeleton } from "../ui/skeleton.tsx";

// The Profile area (docs/design-system.md §0.6, §0.3d; plan M5 step 5c): one
// product's profile as an object page. The website and repository are named
// once, under the title; the product's own properties sit beside the body;
// the body is five tabs: the core facts, its own rules, the lessons its
// confirmed readings taught, the sources the facts are read from, and what
// waits for review since a source changed. Only what needs the person is called out on top.

type View = "facts" | "rules" | "lessons" | "sources" | "review";

/** `/p/:product/profile?view=rules` opens the rules, `?view=lessons` the lessons, `?view=sources` the sources, `?view=review` what waits for review. */
export const profileSearch = (search: Record<string, unknown>): { view?: Exclude<View, "facts"> } =>
  search.view === "rules" || search.view === "lessons" || search.view === "sources" || search.view === "review" ? { view: search.view } : {};

export function ProfilePage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { view?: Exclude<View, "facts"> };
  const { products, product: current } = useCurrentProduct();
  const pid = current?.id ?? "";
  const product = useQuery({ ...q.product(pid), enabled: Boolean(pid), refetchInterval: (query) => (query.state.data?.status === "reading" ? 3000 : false) });
  const review = useQuery({ ...q.sourceReview(pid), enabled: Boolean(pid) });
  const rules = useQuery({ ...q.productRules(pid), enabled: Boolean(pid) });
  const refresh = useRefresh(pid);
  const reread = useMutation({ mutationFn: () => api.post(`/api/products/${pid}/read`), onSuccess: refresh });
  const crumbs = current ? [{ label: current.name, to: "/p/$productId/week", params: { productId: current.id } }, { label: A.profile }] : [{ label: A.profile }];
  if (products.isLoading || (pid && product.isLoading)) return <Page wide crumbs={crumbs}><Skeleton shape="object" /></Page>;
  if (!current) return <Page crumbs={crumbs} title={A.profile}><Quiet title={P.noProduct}><p>{I.noProductsNote}</p><p><Link to="/new">{I.addProduct}</Link></p></Quiet></Page>;
  const p = product.data;
  if (!p) return <Page crumbs={crumbs} title={A.profile}><ErrorText error={products.error ?? product.error} /></Page>;
  const view: View = search.view ?? "facts";
  const setView = (next: View) => { void navigate({ to: "/p/$productId/profile", params: { productId: p.id }, search: next === "facts" ? {} : { view: next }, replace: true }); };
  const stale = review.data?.stale.length ?? 0;
  const proposals = review.data?.candidates.length ?? 0;
  const waitingRules = rules.data?.filter((rule) => rule.status === "proposed").length ?? 0;
  const index = (products.data ?? []).findIndex((candidate) => candidate.id === p.id);
  const readAgain = <Button size="sm" tone="ghost" busy={reread.isPending} disabled={p.status === "reading"} onClick={() => reread.mutate()} title={L.readAgainHelp}>
    <ArrowsClockwiseIcon size={14} /> {L.readAgain}
  </Button>;
  const title = <span className="z-title-mark"><ProductMark productId={p.id} url={p.url} name={p.name} tone={index % 2 === 0 ? 1 : 2} size={24} />{p.name}</span>;
  const meta = <>
    <a className="z-link" href={p.url} target="_blank" rel="noreferrer noopener">{p.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a>
    {p.source.repository && <span className="z-inline"><ServiceLogo service="github" /><a className="z-link" href={`https://github.com/${p.source.repository}`} target="_blank" rel="noreferrer noopener">{p.source.repository}</a></span>}
  </>;
  return <Page wide crumbs={crumbs} title={title} meta={meta} actions={isAccountOnly({ url: p.url, repository: p.source.repository }) ? undefined : readAgain}>
    {p.status === "reading" && <Callout>{L.reading}</Callout>}
    {/* Why the read failed, and where to fix it; reading again stays in the header. */}
    {p.status === "read_failed" && <Callout tone="bad">
      <div className="z-callout-row"><span><strong>{D.sourceFailed}</strong> {p.read_failure ? reasonText(p.read_failure) : D.sourceRecovery}</span>
        {view !== "sources" && <Button size="sm" onClick={() => setView("sources")}>{D.change}</Button>}</div>
    </Callout>}
    <ErrorText error={reread.error} />
    <div className="z-layout-main">
      <div>
        <Tabs value={view} onValueChange={setView} tabs={[
          { value: "facts", label: D.tabs.facts },
          { value: "rules", label: D.tabs.rules, count: waitingRules || undefined },
          { value: "lessons", label: D.tabs.lessons },
          { value: "sources", label: D.tabs.sources },
          { value: "review", label: D.tabs.review, count: review.data ? reviewItems(review.data).length || undefined : undefined },
        ]}>
          <TabPanel value="facts">{view === "facts" && <ProfileFacts product={p} stale={stale} proposals={proposals} onReview={() => setView("review")} />}</TabPanel>
          <TabPanel value="rules">{view === "rules" && <ProductRules productId={p.id} />}</TabPanel>
          <TabPanel value="lessons">{view === "lessons" && <Lessons productId={p.id} />}</TabPanel>
          <TabPanel value="sources">{view === "sources" && <>
            <ProductSources product={p} />
            <LastRead product={p} />
            <Readiness product={p} />
          </>}</TabPanel>
          <TabPanel value="review">{view === "review" && <SourceReview productId={p.id} />}</TabPanel>
        </Tabs>
      </div>
      <ProfileProperties product={p} />
    </div>
  </Page>;
}
